import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import { FadeIn } from "@/components/motion";
import { IMAGES } from "@/config/site";

const STEPS = [
  {
    num: "01",
    title: "Send the referral",
    text: "Use our referral form to share the essentials — it takes a few minutes and reaches our team directly.",
  },
  {
    num: "02",
    title: "We connect",
    text: "We follow up with your team and the family to understand the discharge plan and what home should look like.",
  },
  {
    num: "03",
    title: "The transition begins",
    text: "With everyone aligned, the move home becomes a coordinated step — not a leap of faith.",
  },
];

const INCLUDE = [
  "The patient's current setting and anticipated discharge timing",
  "The reason for referral, in your own words",
  "Any coordination needs your team wants handed off",
  "The best way to reach you for follow-up",
];

export default function Professionals() {
  return (
    <div data-testid="professionals-page">
      <PageHeader
        eyebrow="For Healthcare Professionals"
        lines={["A steady hand", "for the handoff home."]}
        intro="Homevia partners with hospital discharge planners, physicians, and care teams to carry patients safely from your care to their front door."
        testid="professionals-heading"
      />

      <section className="px-6 pb-24 lg:px-10">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeIn>
              <h2 className="font-serif text-4xl tracking-tight text-ink lg:text-5xl">
                Referral, in three steps
              </h2>
            </FadeIn>
            <div className="mt-12 space-y-0">
              {STEPS.map((s, i) => (
                <FadeIn key={s.num} delay={i * 0.1}>
                  <div
                    className={`flex gap-8 border-t border-olive/10 py-8 ${
                      i === STEPS.length - 1 ? "border-b" : ""
                    }`}
                  >
                    <span className="font-display italic text-xl text-teal">({s.num})</span>
                    <div>
                      <h3 className="font-serif text-2xl text-ink">{s.title}</h3>
                      <p className="mt-2 max-w-lg text-olive/75">{s.text}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.25} className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/referral"
                data-testid="professionals-referral-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-deep px-7 py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-olive hover:shadow-xl"
              >
                Submit a Referral <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/consultation"
                data-testid="professionals-consultation-btn"
                className="inline-flex items-center gap-2 rounded-full border border-olive/20 px-7 py-3.5 text-sm font-bold text-olive transition-all duration-300 hover:border-teal-deep hover:bg-sage/10"
              >
                Talk to Our Team <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
          <div className="lg:col-span-5">
            <FadeIn delay={0.15}>
              <div className="relative">
                <div className="absolute -inset-4 rotate-1 rounded-[2.5rem] bg-teal/10" aria-hidden />
                <div className="relative overflow-hidden rounded-[2rem] border border-olive/10">
                  <img
                    src={IMAGES.professionals}
                    alt="Medical professionals discussing patient care together"
                    data-testid="professionals-image"
                    className="h-[380px] w-full object-cover lg:h-[460px]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-olive/25 via-transparent to-transparent" aria-hidden />
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-sand/50 px-6 py-24 lg:px-10 lg:py-28">
        <div className="mx-auto max-w-7xl">
          <FadeIn>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal">Before you refer</p>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl tracking-tight text-ink lg:text-5xl">
              Helpful details to include
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {INCLUDE.map((item, i) => (
              <FadeIn key={item} delay={i * 0.08}>
                <div className="flex items-start gap-4 rounded-3xl border border-olive/10 bg-white p-6 transition-all duration-500 hover:border-teal/50">
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-sage/25">
                    <Check className="h-4 w-4 text-teal-deep" />
                  </span>
                  <p className="text-olive/85">{item}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-olive/60">
              Please don't include detailed medical records in the form — a short summary is enough
              for us to begin the conversation. We'll coordinate the rest with your team directly.
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
